import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const SidebarDocumentNavigator = ({ 
  isOpen = false, 
  onClose,
  contractId = "CT-2024-001"
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(['root']);

  const documentStructure = [
    {
      id: 'root',
      name: 'Contract Documents',
      type: 'folder',
      children: [
        {
          id: 'original',
          name: 'Original Contract',
          type: 'folder',
          children: [
            { id: 'main-contract', name: 'Master Service Agreement.pdf', type: 'file', size: '2.4 MB', modified: '2024-01-15' },
            { id: 'exhibits', name: 'Exhibits A-C.pdf', type: 'file', size: '1.8 MB', modified: '2024-01-15' },
          ]
        },
        {
          id: 'amendments',
          name: 'Amendments',
          type: 'folder',
          children: [
            { id: 'amendment-1', name: 'Amendment 01 - Scope Change.pdf', type: 'file', size: '856 KB', modified: '2024-02-20' },
            { id: 'amendment-2', name: 'Amendment 02 - Timeline Extension.pdf', type: 'file', size: '743 KB', modified: '2024-03-10' },
          ]
        },
        {
          id: 'supporting',
          name: 'Supporting Documents',
          type: 'folder',
          children: [
            { id: 'sow', name: 'Statement of Work.docx', type: 'file', size: '1.2 MB', modified: '2024-01-12' },
            { id: 'pricing', name: 'Pricing Schedule.xlsx', type: 'file', size: '445 KB', modified: '2024-01-18' },
            { id: 'compliance', name: 'Compliance Checklist.pdf', type: 'file', size: '678 KB', modified: '2024-01-20' },
          ]
        },
        {
          id: 'correspondence',
          name: 'Correspondence',
          type: 'folder',
          children: [
            { id: 'email-thread', name: 'Email Thread - Contract Negotiation.pdf', type: 'file', size: '2.1 MB', modified: '2024-01-25' },
            { id: 'meeting-notes', name: 'Meeting Notes - Kickoff.docx', type: 'file', size: '234 KB', modified: '2024-02-01' },
          ]
        }
      ]
    }
  ];

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => 
      prev?.includes(folderId) 
        ? prev?.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const handleFileClick = (file) => {
    console.log('Opening file:', file?.name);
  };

  const handleFileAction = (action, file) => {
    console.log(`${action} file:`, file?.name);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'docx': case 'doc': return 'FileText';
      case 'xlsx': case 'xls': return 'Sheet';
      default: return 'File';
    }
  };

  const renderDocumentTree = (items, level = 0) => {
    return items?.map(item => (
      <div key={item?.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        {item?.type === 'folder' ? (
          <div>
            <button
              onClick={() => toggleFolder(item?.id)}
              className="flex items-center space-x-2 w-full p-2 text-left hover:bg-muted rounded-md transition-legal-fast"
            >
              <Icon 
                name={expandedFolders?.includes(item?.id) ? 'ChevronDown' : 'ChevronRight'} 
                size={14} 
              />
              <Icon name="Folder" size={16} className="text-accent" />
              <span className="text-sm font-medium text-foreground">{item?.name}</span>
            </button>
            {expandedFolders?.includes(item?.id) && item?.children && (
              <div className="mt-1">
                {renderDocumentTree(item?.children, level + 1)}
              </div>
            )}
          </div>
        ) : (
          <div className="group flex items-center justify-between p-2 hover:bg-muted rounded-md transition-legal-fast">
            <button
              onClick={() => handleFileClick(item)}
              className="flex items-center space-x-2 flex-1 text-left"
            >
              <Icon name={getFileIcon(item?.name)} size={16} className="text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{item?.name}</p>
                <p className="text-xs text-muted-foreground">{item?.size} • {item?.modified}</p>
              </div>
            </button>
            <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1 transition-legal">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFileAction('download', item)}
                title="Download"
              >
                <Icon name="Download" size={12} />
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => handleFileAction('share', item)}
                title="Share"
              >
                <Icon name="Share2" size={12} />
              </Button>
            </div>
          </div>
        )}
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />
      {/* Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-80 bg-card border-l border-border shadow-legal-lg z-50 transform transition-transform ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="FolderOpen" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Documents</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Contract Info */}
        <div className="p-4 bg-muted/30 border-b border-border">
          <p className="text-sm font-medium text-foreground">Contract Documents</p>
          <p className="text-xs text-muted-foreground font-mono">{contractId}</p>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <Input
            type="search"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-b border-border">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              iconPosition="left"
              fullWidth
            >
              Upload
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="FolderPlus"
              iconPosition="left"
              fullWidth
            >
              New Folder
            </Button>
          </div>
        </div>

        {/* Document Tree */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderDocumentTree(documentStructure)}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>24 files • 12.4 MB total</span>
            <Button
              variant="ghost"
              size="xs"
              iconName="MoreHorizontal"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SidebarDocumentNavigator;